import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DashboardState,
  ISuperAdminMetric,
  ISuperAdminPlanUsageResponse,
  ITenantAdminMetric,
  ITenantManagerMetric,
  ITenantStaffMetric,
  ITenantCategorySalesResponse,
  ITenantDashboardSummary,
  ITenantLowStockResponse,
  ITenantPurchaseOverviewResponse,
  ITenantRecentOrdersResponse,
  ITenantRecentPurchaseOrder,
  ITenantRecentSaleOrder,
  ITenantSalesOverviewResponse,
  ITenantStockMovementResponse,
  ITenantTopProductsResponse,
} from "src/types/dashboard.types";

type TenantReportParams = {
  days?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  categoryId?: string;
  productId?: string;
  userId?: string;
};

const initialState: DashboardState = {
  metrics: { loading: false, data: null, error: null },
  superAdminMetric: { loading: false, data: null, error: null },
  superAdminPlanUsage: { loading: false, data: null, error: null },
  tenantAdminMetric: { loading: false, data: null, error: null },
  tenantManagerMetric: { loading: false, data: null, error: null },
  tenantStaffMetric: { loading: false, data: null, error: null },
  tenantDashboardSummary: { loading: false, data: null, error: null },
  tenantStockMovement: { loading: false, data: null, error: null },
  tenantSalesOverview: { loading: false, data: null, error: null },
  tenantPurchaseOverview: { loading: false, data: null, error: null },
  tenantCategorySales: { loading: false, data: null, error: null },
  tenantTopProducts: { loading: false, data: null, error: null },
  tenantRecentSales: { loading: false, data: null, error: null },
  tenantRecentPurchases: { loading: false, data: null, error: null },
  tenantLowStockAlerts: { loading: false, data: null, error: null },
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    /** ---------------- SUPER ADMIN ---------------- **/
    getMetricsRequest: (state, action: PayloadAction<any>) => {
      state.metrics.loading = true;
      state.metrics.error = null;
    },
    getMetricsSuccess: (state, action: PayloadAction<any>) => {
      state.metrics.loading = false;
      state.metrics.data = action.payload;
    },
    getMetricsError: (state, action: PayloadAction<string>) => {
      state.metrics.loading = false;
      state.metrics.error = action.payload;
    },

    /** ---------------- SUPER ADMIN ---------------- **/
    getSuperAdminDashboardMetricRequest: (state) => {
      state.superAdminMetric.loading = true;
      state.superAdminMetric.error = null;
    },
    getSuperAdminDashboardMetricRequestSuccess: (
      state,
      action: PayloadAction<ISuperAdminMetric>
    ) => {
      state.superAdminMetric.loading = false;
      state.superAdminMetric.data = action.payload;
    },
    getSuperAdminDashboardMetricRequestError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.superAdminMetric.loading = false;
      state.superAdminMetric.error = action.payload;
    },

    getSuperAdminPlanUsageRequest: (
      state,
      _action: PayloadAction<{ months?: number }>
    ) => {
      state.superAdminPlanUsage.loading = true;
      state.superAdminPlanUsage.error = null;
    },
    getSuperAdminPlanUsageSuccess: (
      state,
      action: PayloadAction<ISuperAdminPlanUsageResponse>
    ) => {
      state.superAdminPlanUsage.loading = false;
      state.superAdminPlanUsage.data = action.payload;
    },
    getSuperAdminPlanUsageError: (state, action: PayloadAction<string>) => {
      state.superAdminPlanUsage.loading = false;
      state.superAdminPlanUsage.error = action.payload;
    },

    /** ---------------- TENANT ADMIN ---------------- **/
    getTenantAdminDashboardMetricRequest: (
      state,
      _action: PayloadAction<string>
    ) => {
      state.tenantAdminMetric.loading = true;
      state.tenantAdminMetric.error = null;
    },
    getTenantAdminDashboardMetricRequestSuccess: (
      state,
      action: PayloadAction<ITenantAdminMetric>
    ) => {
      state.tenantAdminMetric.loading = false;
      state.tenantAdminMetric.data = action.payload;
    },
    getTenantAdminDashboardMetricRequestError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantAdminMetric.loading = false;
      state.tenantAdminMetric.error = action.payload;
    },

    /** ---------------- TENANT MANAGER ---------------- **/
    getTenantManagerDashboardMetricRequest: (
      state,
      _action: PayloadAction<string>
    ) => {
      state.tenantManagerMetric.loading = true;
      state.tenantManagerMetric.error = null;
    },
    getTenantManagerDashboardMetricRequestSuccess: (
      state,
      action: PayloadAction<ITenantManagerMetric>
    ) => {
      state.tenantManagerMetric.loading = false;
      state.tenantManagerMetric.data = action.payload;
    },
    getTenantManagerDashboardMetricRequestError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantManagerMetric.loading = false;
      state.tenantManagerMetric.error = action.payload;
    },

    /** ---------------- TENANT STAFF ---------------- **/
    getTenantStaffDashboardMetricRequest: (
      state,
      _action: PayloadAction<{ tenantId: string; userId: string }>
    ) => {
      state.tenantStaffMetric.loading = true;
      state.tenantStaffMetric.error = null;
    },
    getTenantStaffDashboardMetricRequestSuccess: (
      state,
      action: PayloadAction<ITenantStaffMetric>
    ) => {
      state.tenantStaffMetric.loading = false;
      state.tenantStaffMetric.data = action.payload;
    },
    getTenantStaffDashboardMetricRequestError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantStaffMetric.loading = false;
      state.tenantStaffMetric.error = action.payload;
    },

    /** ---------------- TENANT DASHBOARD SUMMARY ---------------- **/
    getTenantDashboardSummaryRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantDashboardSummary.loading = true;
      state.tenantDashboardSummary.error = null;
    },
    getTenantDashboardSummarySuccess: (
      state,
      action: PayloadAction<ITenantDashboardSummary>
    ) => {
      state.tenantDashboardSummary.loading = false;
      state.tenantDashboardSummary.data = action.payload;
    },
    getTenantDashboardSummaryError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantDashboardSummary.loading = false;
      state.tenantDashboardSummary.error = action.payload;
    },

    /** ---------------- TENANT STOCK MOVEMENT ---------------- **/
    getTenantStockMovementRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantStockMovement.loading = true;
      state.tenantStockMovement.error = null;
    },
    getTenantStockMovementSuccess: (
      state,
      action: PayloadAction<ITenantStockMovementResponse>
    ) => {
      state.tenantStockMovement.loading = false;
      state.tenantStockMovement.data = action.payload;
    },
    getTenantStockMovementError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantStockMovement.loading = false;
      state.tenantStockMovement.error = action.payload;
    },

    /** ---------------- TENANT SALES OVERVIEW ---------------- **/
    getTenantSalesOverviewRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantSalesOverview.loading = true;
      state.tenantSalesOverview.error = null;
    },
    getTenantSalesOverviewSuccess: (
      state,
      action: PayloadAction<ITenantSalesOverviewResponse>
    ) => {
      state.tenantSalesOverview.loading = false;
      state.tenantSalesOverview.data = action.payload;
    },
    getTenantSalesOverviewError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantSalesOverview.loading = false;
      state.tenantSalesOverview.error = action.payload;
    },

    /** ---------------- TENANT PURCHASE OVERVIEW ---------------- **/
    getTenantPurchaseOverviewRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantPurchaseOverview.loading = true;
      state.tenantPurchaseOverview.error = null;
    },
    getTenantPurchaseOverviewSuccess: (
      state,
      action: PayloadAction<ITenantPurchaseOverviewResponse>
    ) => {
      state.tenantPurchaseOverview.loading = false;
      state.tenantPurchaseOverview.data = action.payload;
    },
    getTenantPurchaseOverviewError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantPurchaseOverview.loading = false;
      state.tenantPurchaseOverview.error = action.payload;
    },

    /** ---------------- TENANT CATEGORY SALES ---------------- **/
    getTenantCategorySalesRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantCategorySales.loading = true;
      state.tenantCategorySales.error = null;
    },
    getTenantCategorySalesSuccess: (
      state,
      action: PayloadAction<ITenantCategorySalesResponse>
    ) => {
      state.tenantCategorySales.loading = false;
      state.tenantCategorySales.data = action.payload;
    },
    getTenantCategorySalesError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantCategorySales.loading = false;
      state.tenantCategorySales.error = action.payload;
    },

    /** ---------------- TENANT TOP PRODUCTS ---------------- **/
    getTenantTopProductsRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantTopProducts.loading = true;
      state.tenantTopProducts.error = null;
    },
    getTenantTopProductsSuccess: (
      state,
      action: PayloadAction<ITenantTopProductsResponse>
    ) => {
      state.tenantTopProducts.loading = false;
      state.tenantTopProducts.data = action.payload;
    },
    getTenantTopProductsError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantTopProducts.loading = false;
      state.tenantTopProducts.error = action.payload;
    },

    /** ---------------- TENANT RECENT SALES ---------------- **/
    getTenantRecentSalesRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantRecentSales.loading = true;
      state.tenantRecentSales.error = null;
    },
    getTenantRecentSalesSuccess: (
      state,
      action: PayloadAction<ITenantRecentOrdersResponse<ITenantRecentSaleOrder>>
    ) => {
      state.tenantRecentSales.loading = false;
      state.tenantRecentSales.data = action.payload;
    },
    getTenantRecentSalesError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantRecentSales.loading = false;
      state.tenantRecentSales.error = action.payload;
    },

    /** ---------------- TENANT RECENT PURCHASES ---------------- **/
    getTenantRecentPurchasesRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantRecentPurchases.loading = true;
      state.tenantRecentPurchases.error = null;
    },
    getTenantRecentPurchasesSuccess: (
      state,
      action: PayloadAction<
        ITenantRecentOrdersResponse<ITenantRecentPurchaseOrder>
      >
    ) => {
      state.tenantRecentPurchases.loading = false;
      state.tenantRecentPurchases.data = action.payload;
    },
    getTenantRecentPurchasesError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantRecentPurchases.loading = false;
      state.tenantRecentPurchases.error = action.payload;
    },

    /** ---------------- TENANT LOW STOCK ALERTS ---------------- **/
    getTenantLowStockAlertsRequest: (
      state,
      _action: PayloadAction<TenantReportParams>
    ) => {
      state.tenantLowStockAlerts.loading = true;
      state.tenantLowStockAlerts.error = null;
    },
    getTenantLowStockAlertsSuccess: (
      state,
      action: PayloadAction<ITenantLowStockResponse>
    ) => {
      state.tenantLowStockAlerts.loading = false;
      state.tenantLowStockAlerts.data = action.payload;
    },
    getTenantLowStockAlertsError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.tenantLowStockAlerts.loading = false;
      state.tenantLowStockAlerts.error = action.payload;
    },
  },
});

export const {
  getMetricsRequest,
  getMetricsSuccess,
  getMetricsError,
  getSuperAdminDashboardMetricRequest,
  getSuperAdminDashboardMetricRequestSuccess,
  getSuperAdminDashboardMetricRequestError,
  getSuperAdminPlanUsageRequest,
  getSuperAdminPlanUsageSuccess,
  getSuperAdminPlanUsageError,
  getTenantAdminDashboardMetricRequest,
  getTenantAdminDashboardMetricRequestSuccess,
  getTenantAdminDashboardMetricRequestError,
  getTenantManagerDashboardMetricRequest,
  getTenantManagerDashboardMetricRequestSuccess,
  getTenantManagerDashboardMetricRequestError,
  getTenantStaffDashboardMetricRequest,
  getTenantStaffDashboardMetricRequestSuccess,
  getTenantStaffDashboardMetricRequestError,
  getTenantDashboardSummaryRequest,
  getTenantDashboardSummarySuccess,
  getTenantDashboardSummaryError,
  getTenantStockMovementRequest,
  getTenantStockMovementSuccess,
  getTenantStockMovementError,
  getTenantSalesOverviewRequest,
  getTenantSalesOverviewSuccess,
  getTenantSalesOverviewError,
  getTenantPurchaseOverviewRequest,
  getTenantPurchaseOverviewSuccess,
  getTenantPurchaseOverviewError,
  getTenantCategorySalesRequest,
  getTenantCategorySalesSuccess,
  getTenantCategorySalesError,
  getTenantTopProductsRequest,
  getTenantTopProductsSuccess,
  getTenantTopProductsError,
  getTenantRecentSalesRequest,
  getTenantRecentSalesSuccess,
  getTenantRecentSalesError,
  getTenantRecentPurchasesRequest,
  getTenantRecentPurchasesSuccess,
  getTenantRecentPurchasesError,
  getTenantLowStockAlertsRequest,
  getTenantLowStockAlertsSuccess,
  getTenantLowStockAlertsError,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
